// src/components/ui/about/Faqs.client.tsx

"use client"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "~/components/Accordion"
import { faqs, type FaqItem } from "./faqs.data"

/**
 * Props for the Faqs component
 */
interface FaqsProps {
  /** Optional array of FAQ items to display. Defaults to built-in faqs */
  items?: readonly FaqItem[]
  /** Optional title for the FAQ section */
  title?: string
  /** Optional subtitle/description for the FAQ section */
  subtitle?: string
  /** Optional CSS class name for the container */
  className?: string
}

/**
 * Client-side FAQ component with interactive accordion functionality
 * 
 * This is a client component that provides interactive accordion behavior for FAQs.
 * It uses the Accordion components to create an accessible, keyboard-navigable
 * interface with smooth animations and proper ARIA attributes.
 * 
 * HYDRATION CONSIDERATIONS:
 * - This component does NOT include JSON-LD structured data to prevent duplication
 * - JSON-LD is handled by the SSR fallback component for SEO and non-JS clients
 * - DOM structure matches SSR component for seamless hydration
 * - Data attributes are compatible with SSR component expectations
 * 
 * Features:
 * - Interactive accordion with expand/collapse functionality
 * - Multiple items can be open simultaneously
 * - Keyboard navigation support
 * - Screen reader accessible with proper ARIA attributes
 * - Smooth animations and hover effects
 * - Responsive design with proper spacing
 * - Progressive enhancement over SSR fallback
 * 
 * Usage:
 * - Use this component when you need interactive FAQ functionality
 * - Should be wrapped in Suspense with SSR fallback component
 * - Supports custom FAQ items or uses default AdSights FAQs
 * - Relies on SSR component for SEO-critical structured data
 * 
 * @param props - The component props
 * @returns JSX element containing interactive FAQ accordion without JSON-LD
 */
export function Faqs({
  items = faqs,
  title = "Frequently Asked Questions",
  subtitle,
  className = ""
}: FaqsProps): JSX.Element {
  const defaultSubtitle = (
    <>
      Can&rsquo;t find the answer you&rsquo;re looking for? Don&rsquo;t hesitate
      to get in touch with our{" "}
      <a
        href="/request-access"
        className="font-medium text-indigo-600 hover:text-indigo-300 dark:text-indigo-400"
        aria-label="Contact customer support">
        customer support
      </a>{" "}
      team.
    </>
  )

  return (
    <section 
      aria-labelledby="faq-title"
      className={`mx-auto max-w-7xl px-6 lg:px-8 bg-inherit ${className}`.trim()}
      data-component="faqs"
    >
      <div className="mx-auto max-w-2xl lg:mx-0">
        <h2
          id="faq-title"
          className="text-3xl font-bold tracking-tight sm:text-4xl text-gray-900 dark:text-white">
          {title}
        </h2>
        <p className="mt-4 text-lg leading-8 text-gray-600 dark:text-gray-300">
          {subtitle ?? defaultSubtitle}
        </p>
      </div>
      
      <div className="py-5" data-faq-container>
        <Accordion 
          type="multiple" 
          className="mx-auto"
          aria-label="Frequently asked questions"
        >
          {items.map((item, index) => {
            const itemId = item.id ?? `faq-${index}`
            const questionId = `faq-question-${itemId}`
            const contentId = `faq-content-${itemId}`
            
            return (
              <AccordionItem
                value={item.question}
                key={itemId}
                className="py-3 first:pt-0"
                data-faq-item
                data-faq-id={itemId}
                itemScope
                itemType="https://schema.org/Question"
              >
                <AccordionTrigger 
                  className="text-gray-700 dark:text-gray-200 font-bold"
                  aria-controls={contentId}
                  id={questionId}
                  data-faq-trigger
                >
                  <h3 itemProp="name" className="mb-0">
                    {item.question}
                  </h3>
                </AccordionTrigger>
                <AccordionContent 
                  className="text-gray-600 dark:text-gray-400"
                  id={contentId}
                  aria-labelledby={questionId}
                  role="region"
                  data-faq-content
                  itemScope
                  itemType="https://schema.org/Answer"
                >
                  <div itemProp="text">
                    {item.answer}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )
          })}
        </Accordion>
      </div>
    </section>
  )
}

/**
 * Export FAQ items for use in other components
 */
export { faqs }
export type { FaqItem }

/**
 * Default export for the FAQ component
 */
export default Faqs
